import { Injectable } from '@angular/core';
import { KeyDatesSnapshot } from '../../model/interfaces/key-dates-snapshot.interface';

export interface SnapshotComparisonResult {
  isSnapshotObsolete: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class KeyDatesSnapshotService {
  private _keyDatesSnapshot: KeyDatesSnapshot;

  constructor() {}

  recordSnapshot(snapshot: KeyDatesSnapshot): void {
    this._keyDatesSnapshot = snapshot;
  }

  resetSnapshot(): void {
    this._keyDatesSnapshot = null;
  }

  compareSnapshot(current: KeyDatesSnapshot): SnapshotComparisonResult {
    if (!this._keyDatesSnapshot) {
      return { isSnapshotObsolete: false };
    }

    let isSnapshotObsolete = false;
    let message = '';

    if (this._keyDatesSnapshot.tenor !== current.tenor) {
      isSnapshotObsolete = true;
      message =
        'Tenor has changed since the key dates were refreshed. Please update the key dates.';
    }

    const currentKeyDates = Object.keys(current).filter(
      (key) =>
        key !== 'tradingCalendars' &&
        key !== 'settlementCalendars' &&
        key !== 'tenor'
    );

    if (
      this._keyDatesSnapshot.tradingCalendars?.sort().join() !==
        current.tradingCalendars?.sort().join() &&
      currentKeyDates.some(
        (key) =>
          current[key].calendar === 'TRADING' &&
          current[key].priority === 'relative'
      )
    ) {
      isSnapshotObsolete = true;
      message =
        'Trading calendars have changed since the key dates were refreshed. Please refresh the key dates.';
    }

    if (
      this._keyDatesSnapshot.settlementCalendars?.sort().join() !==
        current.settlementCalendars?.sort().join() &&
      currentKeyDates.some((key) => current[key].calendar === 'SETTLEMENT')
    ) {
      isSnapshotObsolete = true;
      message =
        'Settlement calendars have changed since the key dates were refreshed. Please refresh the key dates.';
    }

    if (isSnapshotObsolete) {
      return {
        isSnapshotObsolete,
        message,
      };
    }

    // Go through all KeyDateDetail objects one by one and for each:
    // 1. If current and snapshot date have `priority` set to `absolute` and their values are the same, then continue to the next KeyDateDetail object.
    // 2. If current and snapshot date have `priority` set to `absolute` and their values are different, then check if the date is set as another date's `anchorDate` in current. If yes, then it is obsolete.
    // 3. If current and snapshot date have `priority` set to `relative` and their values are the same, then continue to the next KeyDateDetail object.
    // 4. If current and snapshot date have `priority` set to `relative` and their values are different then it is obsolete.
    // 5. If current date has `priority` set to `relative` and snapshot date has `priority` set to `absolute`, then it is obsolete.
    // 6. If current date has `priority` set to `absolute` and snapshot date has `priority` set to `relative`, check if the date is set as another date's `anchorDate` in current. If yes, then it is obsolete.

    const keyDateFields = [
      'issueDate',
      'interestStartDate',
      'interestEndDate',
      'redemptionDate',
      'initialObservationDate',
      'finalObservationDate',
      'tradeDate',
      'dtePmtUnadjFirstT',
      'dtePmtUnadjLast0',
      'productFinalisationDate',
    ];

    for (let keyDate of keyDateFields) {
      if (
        current[keyDate]?.priority === 'absolute' &&
        this._keyDatesSnapshot[keyDate]?.priority === 'absolute'
      ) {
        if (
          current[keyDate].toString() !==
            this._keyDatesSnapshot[keyDate].toString() &&
          currentKeyDates.some(
            (key) =>
              current[key].anchorDate === this.getAnchorForDateKey(keyDate) &&
              current[key].priority === 'relative'
          )
        ) {
          return {
            isSnapshotObsolete: true,
            message: `${this.getDisplayNameForDateKey(
              keyDate
            )} has changed since the key dates were refreshed and one or more key dates are defined relative to it. Please refresh the key dates.`,
          };
        }
      }

      if (
        current[keyDate]?.priority === 'relative' &&
        Object.keys(current[keyDate])
          .filter((key) => key !== '_priority')
          .some((key) => current[keyDate][key]) &&
        this._keyDatesSnapshot[keyDate]?.priority === 'relative'
      ) {
        if (
          current[keyDate].toString() !==
          this._keyDatesSnapshot[keyDate].toString()
        ) {
          return {
            isSnapshotObsolete: true,
            message: `${this.getDisplayNameForDateKey(
              keyDate
            )} has changed since the key dates were refreshed. Please refresh the key dates.`,
          };
        }
      }

      if (
        current[keyDate]?.priority === 'relative' &&
        current[keyDate]?.anchorDate &&
        this._keyDatesSnapshot[keyDate].priority === 'absolute'
      ) {
        return {
          isSnapshotObsolete: true,
          message: `${this.getDisplayNameForDateKey(
            keyDate
          )} has changed since the key dates were refreshed. Please refresh the key dates.`,
        };
      }

      if (
        current[keyDate]?.priority === 'absolute' &&
        this._keyDatesSnapshot[keyDate].priority === 'relative'
      ) {
        if (
          currentKeyDates.some(
            (key) =>
              current[key].anchorDate === this.getAnchorForDateKey(keyDate) &&
              current[key].priority === 'relative'
          )
        ) {
          return {
            isSnapshotObsolete: true,
            message: `${this.getDisplayNameForDateKey(
              keyDate
            )} has changed since the key dates were refreshed and one or more key dates are defined relative to it. Please refresh the key dates.`,
          };
        }
      }
    }

    return {
      isSnapshotObsolete: false,
    };
  }

  private getDisplayNameForDateKey(key: string): string {
    switch (key) {
      case 'issueDate':
        return 'Issue Date';
      case 'interestStartDate':
        return 'Interest Start Date';
      case 'interestEndDate':
        return 'Interest End Date';
      case 'redemptionDate':
        return 'Redemption Date';
      case 'initialObservationDate':
        return 'Initial Observation Date';
      case 'finalObservationDate':
        return 'Final Observation Date';
      case 'tradeDate':
        return 'Trade Date';
      case 'productFinalisationDate':
        return 'Product Finalisation Date';
      default:
        return null;
    }
  }

  private getAnchorForDateKey(key: string): string {
    switch (key) {
      case 'issueDate':
        return 'ISSUE_DATE';
      case 'interestStartDate':
        return 'INTEREST_START_DATE';
      case 'interestEndDate':
        return 'INTEREST_END_DATE';
      case 'redemptionDate':
        return 'MATURITY_DATE';
      case 'initialObservationDate':
        return 'INITIAL_FIXING_DATE';
      case 'finalObservationDate':
        return 'FINAL_FIXING_DATE';
      case 'tradeDate':
        return 'TRADE_DATE';
      case 'dtePmtUnadjFirstT':
        return 'END_FIRST_PMT_PERIOD';
      case 'dtePmtUnadjLast0':
        return 'START_LAST_PMT_PERIOD';
      case 'productFinalisationDate':
        return 'PRODUCT_FINALISATION_DATE';
      default:
        return null;
    }
  }
}
